/**
 * Helpers de dinheiro do módulo Financeiro (épico #72).
 *
 * Convenção (decisão do owner 2026-06-21): armazenamento e cálculos em
 * **cents inteiros**; exibição e entrada sempre em **0.00 (2 casas)**.
 * Para exibir valor com símbolo/locale use `formatCurrencyCents`
 * (`features/dashboard-finance/formatters.ts`). Aqui ficam o parse da
 * entrada do usuário e o formato plano do campo de edição.
 */

/** Cents → string plana "0.00" (ponto decimal, sem separador de milhar). */
export function formatCentsToInput(cents: number): string {
  return (cents / 100).toFixed(2);
}

/**
 * Faz parse de um valor digitado para cents inteiros. Aceita ponto ou
 * vírgula decimal e separador de milhar (`1800.00`, `1800,00`, `1.800,00`,
 * `1,800.00`, `-12,34`). Retorna `null` se não for um número válido.
 *
 * Origem: #73 — commit 25cf69b6a, 2026-06-21 (módulo Financeiro interno).
 *
 * Contrato: o separador decimal é o ÚLTIMO de `.`/`,` que aparece; o outro é
 * milhar. Só um separador → ele é o decimal (`1.800` vira 1,80, não 1800).
 * Arredonda para o centavo mais próximo.
 *
 * Usado por: `features/finance-entries/FinanceEntryEditorDialog.tsx` e
 * `features/finance-accounts/FinanceAccountEditorDialog.tsx`.
 *
 * Dicas: `1.800` com ponto como milhar (hábito BR) é lido como 1,80 — a regra
 * acima é deliberada para aceitar `12.5`; quem digita milhar sem decimais
 * deve usar `1800`.
 */
export function parseInputToCents(raw: string): number | null {
  const s = raw.trim();
  if (s === '') return null;
  const negative = s.startsWith('-');
  const body = s.replace(/[^0-9.,]/g, '');
  if (body === '') return null;

  const lastDot = body.lastIndexOf('.');
  const lastComma = body.lastIndexOf(',');
  let decimalSep = '';
  if (lastDot >= 0 && lastComma >= 0) decimalSep = lastDot > lastComma ? '.' : ',';
  else if (lastComma >= 0) decimalSep = ',';
  else if (lastDot >= 0) decimalSep = '.';

  let normalized: string;
  if (decimalSep === '') {
    normalized = body.replace(/[.,]/g, '');
  } else {
    const thousandSep = decimalSep === '.' ? ',' : '.';
    normalized = body.split(thousandSep).join('').replace(decimalSep, '.');
  }

  const value = Number(normalized);
  if (!Number.isFinite(value)) return null;
  const cents = Math.round(value * 100);
  return negative ? -cents : cents;
}
